import { Match } from 'effect';
import { last, sortedIndex } from 'lodash';
import moment from 'moment';
import type { FrequencyEnum, HistoricalPortfolioAllocationItem, Portfolio } from 'venn-api';
import { FREQUENCY_TO_PERIOD, toEndOfPeriod } from '../dates/utils';
import { assert, assertNotNil } from '../utils';
import { ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT, AsOfDateSetting, FormattedDateString } from './types';

export const CURRENT_AS_OF_DATE = AsOfDateSetting.Current();
export const LAST_UPLOADED_AS_OF_DATE = AsOfDateSetting.LastUploaded();

export const isHistoricalPortfolio = (portfolio: Portfolio) => {
  return portfolio.historical;
};

export const getAllocationsAsOfDateTimestampFromSetting = ({
  asOfDateSetting,
  currentDate,
  lastUploadedDate,
}: {
  asOfDateSetting: AsOfDateSetting;
  currentDate: number | undefined;
  lastUploadedDate: number | undefined;
}): number | undefined => {
  return Match.value(asOfDateSetting).pipe(
    Match.tag('Current', () => currentDate),
    Match.tag('LastUploaded', () => lastUploadedDate),
    Match.tag('Date', ({ date }) => moment.utc(date, ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT).endOf('day').valueOf()),
    Match.exhaustive,
  );
};

export const getCurrentDate = (allSortedAllocationDates: number[] | undefined) => {
  return last(allSortedAllocationDates);
};

export const getLastUploadedDate = (allSortedRebalancingDates: number[] | undefined) => {
  return last(allSortedRebalancingDates);
};

export const ALLOCATION_PANEL_AS_OF_DATE_DISPLAY_FORMAT = 'D MMM YYYY';

export type HistoricalPortfolioNodeAllocationType = 'USER_UPLOADED' | 'DRIFTED' | 'FORWARD_FILLED';

/**
 * @param selectedAsOfDate currently selected as of date in allocator panel
 * @param uploadedAllocationDates all sorted uploaded allocation dates
 * @param frequency return frequency of the fund (not the portfolio)
 */
export const getHistoricalPortfolioNodeAllocationType = (
  selectedAsOfDate: number,
  uploadedAllocationDates: number[],
  frequency: FrequencyEnum,
): HistoricalPortfolioNodeAllocationType => {
  assert(uploadedAllocationDates.length > 0, 'uploadedAllocationDates should not be empty');

  const index = sortedIndex(uploadedAllocationDates, selectedAsOfDate);

  // If selectedAsOfDate exists in uploadedAllocationDates it means we are showing user uploaded allocations
  if (index < uploadedAllocationDates.length && uploadedAllocationDates[index] === selectedAsOfDate) {
    return 'USER_UPLOADED';
  }

  // If selectedAsOfDate is before the first uploaded allocation date it means this fund didn't exist at that time.
  assert(index > 0, 'selectedAsOfDate should be after the earliest allocation date');

  const latestAllocationDateSmallerThanSelectedAsOfDate = assertNotNil(uploadedAllocationDates[index - 1]);

  const roundedTimestamp = toEndOfPeriod(
    latestAllocationDateSmallerThanSelectedAsOfDate,
    FREQUENCY_TO_PERIOD[frequency],
  );

  /**
   * If rounded (snapped) timestamp matched with selected as of date it means we are showing forward filled allocations.
   * This can happen when user uploads intra-month allocations for monthly investment and we snap intra-month dates to end of month.
   */
  if (roundedTimestamp === selectedAsOfDate) {
    return 'FORWARD_FILLED';
  }

  // Otherwise we are showing drifted allocations
  return 'DRIFTED';
};

export const serializeAsOfDateSetting = (value: AsOfDateSetting): string => {
  return Match.value(value).pipe(
    Match.tag('Current', () => 'Current'),
    Match.tag('LastUploaded', () => 'LastUploaded'),
    Match.tag('Date', ({ date }) => date),
    Match.exhaustive,
  );
};

export const UNSAFE_deserializeAsOfDateSetting = (value: string): AsOfDateSetting => {
  return Match.value(value).pipe(
    Match.when('Current', () => AsOfDateSetting.Current()),
    Match.when('LastUploaded', () => AsOfDateSetting.LastUploaded()),
    // this method is unsafe because it throws if the date is invalid
    // we should ideally be returning an error through the effect system
    // or even an Option type... but we we don't have consensus on patterns to handle
    // errors yet, so I'm leaving this unsafe.
    Match.orElse((date) => AsOfDateSetting.Date({ date: FormattedDateString(date) })),
  );
};

const safeDivide = (a: number, b: number): number => {
  if (Number.isNaN(b) || !Number.isFinite(b) || b === 0.0) {
    return 0.0;
  }
  return a / b;
};

export const convertAllocationsToPercentages = (
  items: HistoricalPortfolioAllocationItem[],
): HistoricalPortfolioAllocationItem[] => {
  // map from date (timestamp) to total portfolio allocation at that date
  const totals = new Map<number, number>();

  for (const item of items) {
    for (const [timestamp, value] of item.allocations) {
      const total = totals.get(assertNotNil(timestamp)) ?? 0;
      totals.set(assertNotNil(timestamp), total + assertNotNil(value));
    }
  }

  return items.map((item) => {
    return {
      ...item,
      allocations: item.allocations.map(([timestamp, value]) => {
        const total = assertNotNil(totals.get(assertNotNil(timestamp)));
        return [assertNotNil(timestamp), safeDivide(assertNotNil(value), total)];
      }),
    };
  });
};
